import { c as _c } from "react/compiler-runtime";
import { cn } from "cn";
import type * as React from "react";
interface TypographyProps extends React.HTMLAttributes<HTMLElement> {
  as?: React.ElementType;
}

// ─── Display ──────────────────────────────────────────────────────────────────
// Season Mix VF · hero sections, large marketing moments · never in dense product UI

interface DisplayProps extends TypographyProps {
  size: "large" | "medium" | "small" | "xsmall";
}
const displaySizeMap = {
  large: "display-lg",
  medium: "display-md",
  small: "display-sm",
  xsmall: "display-xs"
} as const;
const displayDefaultElement: Record<DisplayProps["size"], React.ElementType> = {
  large: "div",
  // expressive/visual — use as="h1" when it is the structural page title
  medium: "div",
  // expressive/visual — use as="h1" when it is the main screen title
  small: "div",
  // expressive/visual — use as when it functions as a heading
  xsmall: "p" // pull quote
};
function Display(t0) {
  const $ = _c(12);
  let Tag;
  let className;
  let props;
  let size;
  if ($[0] !== t0) {
    ({
      size,
      as: Tag,
      className,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = Tag;
    $[2] = className;
    $[3] = props;
    $[4] = size;
  } else {
    Tag = $[1];
    className = $[2];
    props = $[3];
    size = $[4];
  }
  const Element = Tag ?? displayDefaultElement[size];
  const t1 = displaySizeMap[size];
  let t2;
  if ($[5] !== className || $[6] !== t1) {
    t2 = cn(t1, className);
    $[5] = className;
    $[6] = t1;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  let t3;
  if ($[8] !== Element || $[9] !== props || $[10] !== t2) {
    t3 = <Element className={t2} {...props} />;
    $[8] = Element;
    $[9] = props;
    $[10] = t2;
    $[11] = t3;
  } else {
    t3 = $[11];
  }
  return t3;
}

// ─── Heading ──────────────────────────────────────────────────────────────────
// Season Sans VF · page titles, section headers, card titles

interface HeadingProps extends TypographyProps {
  size: "large" | "medium" | "small" | "xsmall";
}
const headingSizeMap = {
  large: "heading-lg",
  medium: "heading-md",
  small: "heading-sm",
  xsmall: "heading-xs"
} as const;
const headingDefaultElement: Record<HeadingProps["size"], React.ElementType> = {
  large: "h1",
  // page title
  medium: "h2",
  // section header, card title
  small: "h3",
  // sub-section header
  xsmall: "h4" // small label acting as a header
};
function Heading(t0) {
  const $ = _c(12);
  let Tag;
  let className;
  let props;
  let size;
  if ($[0] !== t0) {
    ({
      size,
      as: Tag,
      className,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = Tag;
    $[2] = className;
    $[3] = props;
    $[4] = size;
  } else {
    Tag = $[1];
    className = $[2];
    props = $[3];
    size = $[4];
  }
  const Element = Tag ?? headingDefaultElement[size];
  const t1 = headingSizeMap[size];
  let t2;
  if ($[5] !== className || $[6] !== t1) {
    t2 = cn(t1, className);
    $[5] = className;
    $[6] = t1;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  let t3;
  if ($[8] !== Element || $[9] !== props || $[10] !== t2) {
    t3 = <Element className={t2} {...props} />;
    $[8] = Element;
    $[9] = props;
    $[10] = t2;
    $[11] = t3;
  } else {
    t3 = $[11];
  }
  return t3;
}

// ─── Body ─────────────────────────────────────────────────────────────────────
// Season Sans VF · paragraphs, job descriptions, profile bios, helper text

interface BodyProps extends TypographyProps {
  size?: "large" | "medium" | "small";
}
const bodySizeMap = {
  large: "body-lg",
  medium: "body-md",
  small: "body-sm"
} as const;
function Body(t0) {
  const $ = _c(12);
  let className;
  let props;
  let t1;
  let t2;
  if ($[0] !== t0) {
    ({
      size: t1,
      as: t2,
      className,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = props;
    $[3] = t1;
    $[4] = t2;
  } else {
    className = $[1];
    props = $[2];
    t1 = $[3];
    t2 = $[4];
  }
  const size = t1 === undefined ? "medium" : t1;
  const Tag = t2 === undefined ? "p" : t2;
  const t3 = bodySizeMap[size];
  let t4;
  if ($[5] !== className || $[6] !== t3) {
    t4 = cn(t3, className);
    $[5] = className;
    $[6] = t3;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  let t5;
  if ($[8] !== Tag || $[9] !== props || $[10] !== t4) {
    t5 = <Tag className={t4} {...props} />;
    $[8] = Tag;
    $[9] = props;
    $[10] = t4;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  return t5;
}

// ─── Label ────────────────────────────────────────────────────────────────────
// Season Sans VF · button text, input labels, nav items, tabs
// Rule: if the user clicks it, selects it, or fills it in — it's a Label

interface LabelProps extends TypographyProps {
  size?: "large" | "medium" | "small";
}
const labelSizeMap = {
  large: "label-lg",
  medium: "label-md",
  small: "label-sm"
} as const;
function Label(t0) {
  const $ = _c(12);
  let className;
  let props;
  let t1;
  let t2;
  if ($[0] !== t0) {
    ({
      size: t1,
      as: t2,
      className,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = props;
    $[3] = t1;
    $[4] = t2;
  } else {
    className = $[1];
    props = $[2];
    t1 = $[3];
    t2 = $[4];
  }
  const size = t1 === undefined ? "medium" : t1;
  const Tag = t2 === undefined ? "span" : t2;
  const t3 = labelSizeMap[size];
  let t4;
  if ($[5] !== className || $[6] !== t3) {
    t4 = cn(t3, className);
    $[5] = className;
    $[6] = t3;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  let t5;
  if ($[8] !== Tag || $[9] !== props || $[10] !== t4) {
    t5 = <Tag className={t4} {...props} />;
    $[8] = Tag;
    $[9] = props;
    $[10] = t4;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  return t5;
}

// ─── Caption ──────────────────────────────────────────────────────────────────
// Season Sans VF · timestamps, counts, helper text under form fields
// Always subordinate — never the main message on a screen

interface CaptionProps extends TypographyProps {
  size?: "medium" | "small";
}
const captionSizeMap = {
  medium: "caption-md",
  small: "caption-sm"
} as const;
function Caption(t0) {
  const $ = _c(12);
  let className;
  let props;
  let t1;
  let t2;
  if ($[0] !== t0) {
    ({
      size: t1,
      as: t2,
      className,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = props;
    $[3] = t1;
    $[4] = t2;
  } else {
    className = $[1];
    props = $[2];
    t1 = $[3];
    t2 = $[4];
  }
  const size = t1 === undefined ? "medium" : t1;
  const Tag = t2 === undefined ? "span" : t2;
  const t3 = captionSizeMap[size];
  let t4;
  if ($[5] !== className || $[6] !== t3) {
    t4 = cn(t3, className);
    $[5] = className;
    $[6] = t3;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  let t5;
  if ($[8] !== Tag || $[9] !== props || $[10] !== t4) {
    t5 = <Tag className={t4} {...props} />;
    $[8] = Tag;
    $[9] = props;
    $[10] = t4;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  return t5;
}

// ─── Eyebrow ──────────────────────────────────────────────────────────────────
// Season Sans VF · short category tag above a headline ("Featured Companies", "Step 1 of 3")
// Rule: one line only, never more than 3–4 words

function Eyebrow(t0) {
  const $ = _c(10);
  let className;
  let props;
  let t1;
  if ($[0] !== t0) {
    ({
      as: t1,
      className,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = props;
    $[3] = t1;
  } else {
    className = $[1];
    props = $[2];
    t1 = $[3];
  }
  const Tag = t1 === undefined ? "span" : t1;
  let t2;
  if ($[4] !== className) {
    t2 = cn("eyebrow", className);
    $[4] = className;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  let t3;
  if ($[6] !== Tag || $[7] !== props || $[8] !== t2) {
    t3 = <Tag className={t2} {...props} />;
    $[6] = Tag;
    $[7] = props;
    $[8] = t2;
    $[9] = t3;
  } else {
    t3 = $[9];
  }
  return t3;
}

// ─── Mono ─────────────────────────────────────────────────────────────────────
// Basier Square Mono · code snippets, inline code, numeric data in tables

interface MonoProps extends TypographyProps {
  heading?: boolean;
}
function Mono(t0) {
  const $ = _c(12);
  let className;
  let props;
  let t1;
  let t2;
  if ($[0] !== t0) {
    ({
      heading: t1,
      as: t2,
      className,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = props;
    $[3] = t1;
    $[4] = t2;
  } else {
    className = $[1];
    props = $[2];
    t1 = $[3];
    t2 = $[4];
  }
  const heading = t1 === undefined ? false : t1;
  const Tag = t2 === undefined ? "code" : t2;
  const t3 = heading ? "mono-heading" : "mono";
  let t4;
  if ($[5] !== className || $[6] !== t3) {
    t4 = cn(t3, className);
    $[5] = className;
    $[6] = t3;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  let t5;
  if ($[8] !== Tag || $[9] !== props || $[10] !== t4) {
    t5 = <Tag className={t4} {...props} />;
    $[8] = Tag;
    $[9] = props;
    $[10] = t4;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  return t5;
}
export { Display, Heading, Body, Label, Caption, Eyebrow, Mono };
export type { DisplayProps, HeadingProps, BodyProps, LabelProps, CaptionProps, MonoProps };